import React, { Component } from 'react'
import styled, { css } from 'styled-components'
import Link from 'gatsby-link'
import { rgba } from 'polished'
import Form from 'react-dynamic-form'

import Input from '../Input'

import { hoverState } from '../../styles/global'
import { paragraph, caption } from '../../styles/type'
import { media } from '../../styles/utils'
import { grey } from '../../styles/colors'
import { email } from '../../utils/validators'
import { NEWSLETTER_WEBHOOK_URL } from '../../constants'

const ArrowRightIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 12H19M13 6L19 12L13 18" stroke="#4D402E" strokeLinecap="square" strokeLinejoin="round"/>
    </svg>
)


class FooterBlock extends Component {

    constructor(props) {
        super(props)
        this.newsletterForm = React.createRef()
    }

    state = {
        newsletterComplete: false,
        newsletterError: null,
    }

    handleNewsletterSubmit = (fields, resetForm) => {
        const emailValue = fields['email'].value

        // Form-encoded body avoids a CORS preflight against Zapier's catch hook.
        const body = new URLSearchParams()
        body.append('email', emailValue)
        body.append('source', 'footer')

        fetch(NEWSLETTER_WEBHOOK_URL, {
            method: 'POST',
            body,
        }).then((r) => {
            if (r.ok) {
                this.setState({ newsletterComplete: true, newsletterError: null })
                resetForm()
            } else {
                this.setState({ newsletterError: 'Something went wrong. Please try again.' })
            }
        }).catch(() => {
            this.setState({ newsletterError: 'Something went wrong. Please try again.' })
        })
    }

    render() {
        const { newsletterComplete, newsletterError } = this.state

        const newsletterFields = {
            fields: [
                {
                    type: 'text',
                    name: 'email',
                    label: 'Email Address',
                    validator: email,
                    errorMessage: 'Please enter a valid email address',
                },
            ]
        }

        return (
            <Wrapper style={this.props.style} className={this.props.className}>
                <TopBorder />

                <Inner>
                    <ContactColumn>
                        <ColumnLabel>Contact</ColumnLabel>

                        <InfoBlock>
                            Paul Conrad Architects<br/>
                            3–5 Jessie St, Cremorne<br/>
                            Victoria 3121 Australia
                        </InfoBlock>

                        <InfoBlock>
                            Nominated Architect - Paul Conrad<br/>
                            NSW Architect Registration Number DR 15783<br/>
                            VIC Architect Registration Number 51125<br/>
                            WA Architect Registration Number 3894
                        </InfoBlock>

                        <InfoBlock>
                            <div><InfoLabel>Email:</InfoLabel> <a href="mailto:studio@paulconradarchitects.com">studio@paulconradarchitects.com</a></div>
                            <div><InfoLabel>Telephone:</InfoLabel> +61 (3) 9421 6103</div>
                            <div><InfoLabel>Instagram:</InfoLabel> <a href="https://instagram.com/paulconradarchitects" target="_blank" rel="noopener noreferrer">@paulconradarchitects</a></div>
                        </InfoBlock>

                        <CareersBlock>
                            At Paul Conrad Architects we are always looking for talented Architects and Interior Designers. If you would like to enquire about joining our team, please email your CV to <a href="mailto:careers@paulconradarchitects.com">careers@paulconradarchitects.com</a> (maximum 8MB file size).
                        </CareersBlock>

                        <PrivacyLink to="/privacy-policy">Privacy Policy</PrivacyLink>
                    </ContactColumn>

                    <SubscribeColumn>
                        <SubscribeHeading>Stay Connected</SubscribeHeading>

                        {newsletterComplete ? (
                            <SubscribeThanks>Thanks — you're subscribed.</SubscribeThanks>
                        ) : (
                            <SubscribeFormRow>
                                <SubscribeFormInputWrap>
                                    <Form
                                        ref={this.newsletterForm}
                                        data={newsletterFields}
                                        styles={subscribeFormStyles}
                                        renderInput={<Input />}
                                        renderSubmit={false}
                                        onSubmit={this.handleNewsletterSubmit}
                                        moveToInvalidField={false}
                                    />
                                </SubscribeFormInputWrap>
                                <SubscribeArrow
                                    onClick={() => {
                                        this.newsletterForm.current && this.newsletterForm.current.handleSubmit()
                                    }}
                                    aria-label="Subscribe"
                                >
                                    <ArrowRightIcon />
                                </SubscribeArrow>
                            </SubscribeFormRow>
                        )}

                        {newsletterError && (
                            <SubscribeError>{newsletterError}</SubscribeError>
                        )}
                    </SubscribeColumn>
                </Inner>
            </Wrapper>
        )
    }
}


const Wrapper = styled.footer`
    /* Full-viewport-width band; content inside is capped at 1200px. */
    position: relative;
    z-index: 3;
    width: 100%;
    /* Inherits the page colour rather than being its own grey slab — the
       hairline TopBorder is what separates it now. */
    background: transparent;
    box-sizing: border-box;

    /* Opt out of the site-wide "indent subsequent paragraphs" rule */
    * {
        text-indent: 0 !important;
    }
`

const TopBorder = styled.div`
    width: 100%;
    height: 1px;
    background: ${rgba('#595850', 0.15)};
`

const Inner = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 10rem 2.5rem;
    box-sizing: border-box;

    ${media.tablet`
        flex-direction: column;
        padding: 6rem 2.5rem;
    `}

    ${media.phone`
        padding: 6rem 40px;
    `}
`

const ContactColumn = styled.div`
    flex: 1 1 50%;
    padding-right: 4rem;
    box-sizing: border-box;

    ${media.tablet`
        padding-right: 0;
        margin-bottom: 5rem;
    `}
`

const SubscribeColumn = styled.div`
    flex: 1 1 50%;
    box-sizing: border-box;
`

const ColumnLabel = styled.div`
    ${paragraph};
    color: ${grey};
    margin-bottom: 4rem;
`

const InfoBlock = styled.div`
    ${paragraph};
    color: ${grey};
    margin-bottom: 3rem;

    a { color: ${grey}; ${hoverState}; }
`

const InfoLabel = styled.span`
    color: ${rgba('#595850', 0.55)};
`

const CareersBlock = styled.div`
    ${caption};
    color: ${rgba('#595850', 0.55)};
    max-width: 340px;
    margin-bottom: 4rem;

    a { color: ${rgba('#595850', 0.7)}; text-decoration: underline; }
`

const PrivacyLink = styled(Link)`
    ${caption};
    color: ${rgba('#595850', 0.55)};
    ${hoverState};
`

const SubscribeHeading = styled.div`
    font-family: 'Scto';
    font-weight: 300;
    font-size: 22px;
    line-height: 28px;
    color: ${grey};
    margin-bottom: 4rem;

    ${media.laptop`
        font-size: 18px;
        line-height: 24px;
    `}
`

const SubscribeFormRow = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    border-bottom: 1px solid ${rgba('#595850', 0.4)};

    .dynamic-field { margin-bottom: 0 !important; }
    .field-wrapper {
        border-bottom: 0 !important;
        margin-bottom: 0 !important;
    }
`

const SubscribeFormInputWrap = styled.div`
    flex: 1;
    min-width: 0;
`

const SubscribeArrow = styled.button`
    background: transparent;
    border: 0;
    padding: 0;
    margin-left: 16px;
    display: flex;
    align-items: center;
    cursor: pointer;

    svg { display: block; }
    &:hover { opacity: 0.7; }
`

const SubscribeThanks = styled.div`
    ${paragraph};
    color: ${grey};
    padding: 12px 0;
`

const SubscribeError = styled.div`
    ${caption};
    color: #F03232;
    margin-top: 8px;
`

const subscribeFormStyles = css`
    .dynamic-field {
        margin-bottom: 0;
        .field-wrapper {
            border-bottom: 0;
            margin-bottom: 0;
        }
    }
`


export default FooterBlock



// WEBPACK FOOTER //
// ./src/components/FooterBlock/FooterBlock.js